import {isLoggedIn} from '@github-ui/client-env'
import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {issueUrl} from '@github-ui/issue-viewer/Urls'
import {useCachePreheatIssueViewer} from '@github-ui/issue-viewer/UseCachePreheatIssueViewer'
import type {NavigateOptions, To} from '@github-ui/react-router'
import {ScopedCommands} from '@github-ui/ui-commands'
import type {AnalyticsEvent} from '@github-ui/use-analytics'
import React, {type ReactElement, useCallback, useMemo} from 'react'
import {graphql, useFragment} from 'react-relay/hooks'

import type {QUERY_FIELDS} from '../constants/queries'
import type {IssueItem$key} from './__generated__/IssueItem.graphql'
import type {IssueRow$key} from './__generated__/IssueRow.graphql'
import type {IssueRowSecondaryQuery$data} from './__generated__/IssueRowSecondaryQuery.graphql'
import {IssueItem, IssueItemQuery} from './IssueItem'
import styles from './IssueRow.module.css'
import {IssueRowSubscriber} from './IssueRowSubscription'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssuesIndexSecondaryGraphqlQuery = graphql`
  query IssueRowSecondaryQuery($nodes: [ID!]!, $assigneePageSize: Int = 10, $includeReactions: Boolean = false) {
    nodes(ids: $nodes) {
      id
      __typename
      ... on Issue {
        # eslint-disable-next-line relay/must-colocate-fragment-spreads
        ...IssueOrPullRequestUnreadIndicator @dangerously_unaliased_fixme
        ...IssueItemMetadata
          @dangerously_unaliased_fixme
          @arguments(assigneePageSize: $assigneePageSize, includeReactions: $includeReactions)
        ...IssueItemSubIssuesSummary @dangerously_unaliased_fixme
        ...IssueItemBlockedBy
      }
      ... on PullRequest {
        # eslint-disable-next-line relay/must-colocate-fragment-spreads
        ...IssueOrPullRequestUnreadIndicator @dangerously_unaliased_fixme
        # eslint-disable-next-line relay/must-colocate-fragment-spreads
        ...PullRequestItemMetadata
          @dangerously_unaliased_fixme
          @arguments(assigneePageSize: $assigneePageSize, includeReactions: $includeReactions)
        # eslint-disable-next-line relay/must-colocate-fragment-spreads
        ...PullRequestRowSecondary @dangerously_unaliased_fixme
        # eslint-disable-next-line relay/must-colocate-fragment-spreads
        ...CheckRunStatusFromPullRequest @dangerously_unaliased_fixme
      }
    }
  }
`

interface Props {
  issueKey: IssueRow$key
  listItemMetadata?: IssueRowSecondaryQuery$data | null
  // this is passed as an object and it comes from the app payload
  scopedRepository?: {id: string; name: string; owner: string; is_archived: boolean}
  /**
   * Href getter for the metadata badge links
   * @param type - metadata type
   * @param name - name of the metadata
   * @returns URL to the metadata
   */
  getMetadataHref: (type: keyof typeof QUERY_FIELDS, name: string) => string
  getAdditiveMetadataHref: (type: keyof typeof QUERY_FIELDS, name: string) => string
  /**
   * Href getter for the issue field value badge links.
   * @param fieldName - name of the issue field (used to build the `field.<name>:` qualifier)
   * @param queryValue - the value to put in the search query. This is the untruncated
   *   display value for text/single-select/number fields, and the raw ISO calendar
   *   date (`YYYY-MM-DD`) for date fields so the resulting search matches backend-
   *   stored values rather than the user-facing formatted string.
   * @returns URL that filters the surrounding list by the clicked field value
   */
  getIssueFieldHref?: (fieldName: string, queryValue: string) => string
  onSelect?: (selected: boolean) => void
  onNavigate: (to: To, options?: NavigateOptions) => void
  onSidePanelNavigate?: (issue: ItemIdentifier) => void
  onSelectRow: (payload?: {[key: string]: unknown} | AnalyticsEvent | undefined) => void
  isActive: boolean
  isSelected?: boolean
  sortingItemSelected: string
  as?: React.ElementType
  role?: string
  ref?: React.Ref<HTMLAnchorElement>
  preheatEnabled?: boolean
}

const IssueRowInternal = ({
  ref,
  isActive,
  isSelected,
  issueKey,
  scopedRepository,
  getMetadataHref,
  getAdditiveMetadataHref,
  getIssueFieldHref,
  onSelect,
  onNavigate,
  onSidePanelNavigate,
  onSelectRow,
  sortingItemSelected,
  listItemMetadata,
  preheatEnabled = true,
  ...props
}: Props): ReactElement => {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueRow on Issue
      @argumentDefinitions(
        labelPageSize: {type: "Int!"}
        fetchRepository: {type: "Boolean!"}
        includeMilestone: {type: "Boolean!", defaultValue: true}
        showIssueFieldPills: {type: "Boolean!", defaultValue: false}
      ) {
        ...IssueItem
          @arguments(
            labelPageSize: $labelPageSize
            includeMilestone: $includeMilestone
            showIssueFieldPills: $showIssueFieldPills
          )
        number
        repository @include(if: $fetchRepository) {
          name
          owner {
            login
          }
        }
      }
    `,
    issueKey,
  )
  const issueItemKey = data as IssueItem$key
  const issueItemData = useFragment(IssueItemQuery, issueItemKey)

  // This is a fallback in case the repository is not passed
  const repositoryOwner = scopedRepository ? scopedRepository.owner : data.repository?.owner?.login || ''
  const repositoryName = scopedRepository ? scopedRepository.name : data.repository?.name || ''

  useCachePreheatIssueViewer({
    owner: repositoryOwner,
    repo: repositoryName,
    number: data.number,
    source: 'IssueRow',
    enabled: preheatEnabled,
  })

  const url = issueUrl({owner: repositoryOwner, repo: repositoryName, number: data.number})

  const navigateToIssue = useCallback(() => {
    if (onSidePanelNavigate) {
      onSidePanelNavigate({number: data.number, repo: repositoryName, owner: repositoryOwner, type: 'Issue'})
    } else {
      onNavigate(url)
    }
  }, [onSidePanelNavigate, data.number, repositoryName, repositoryOwner, onNavigate, url])

  const handleNavigation = useCallback(
    (event: React.MouseEvent | React.KeyboardEvent) => {
      onSelectRow({type: issueItemData.__typename})

      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.ctrlKey || event.metaKey) return
      event.stopPropagation()
      event.preventDefault()
      navigateToIssue()
    },
    [issueItemData.__typename, navigateToIssue, onSelectRow],
  )

  const toggleSelection = useCallback(() => {
    onSelect?.(!isSelected)
  }, [onSelect, isSelected])

  const commands = useMemo(
    () => ({
      'list-view-items-issues-prs:open-focused-item': navigateToIssue,
      'list-view-items-issues-prs:toggle-focused-item-selection': toggleSelection,
    }),
    [navigateToIssue, toggleSelection],
  )

  return (
    <ScopedCommands commands={commands} className={styles.row}>
      {isLoggedIn() && <IssueRowSubscriber issueId={issueItemData.id} />}
      <IssueItem
        isActive={isActive}
        itemKey={issueItemKey}
        listItemMetadata={listItemMetadata}
        isSelected={isSelected}
        showCommentCount
        showRepository={!scopedRepository}
        repositoryOwner={repositoryOwner}
        repositoryName={repositoryName}
        showAssignees
        sortingItemSelected={sortingItemSelected}
        getMetadataHref={getMetadataHref}
        getAdditiveMetadataHref={getAdditiveMetadataHref}
        getIssueFieldHref={getIssueFieldHref}
        onSelect={onSelect}
        onClick={handleNavigation}
        href={url}
        ref={ref}
        {...props}
      />
    </ScopedCommands>
  )
}

IssueRowInternal.displayName = 'IssueRowInternal'

export const IssueRow = React.memo(IssueRowInternal)
